import * as z from "zod";

export type Overwrite<T, K extends keyof T, NewType> = Omit<T, K> & { [P in K]: NewType };

export const dateSchema = z.preprocess((arg) => {
  if (typeof arg === "string" || arg instanceof Date) {
    const d = new Date(arg);
    if (!isNaN(d.getTime())) return d;
  }
  return arg;
}, z.date());

export const regionWithCoordsSchema = z.object({
  id: z.string(),
  name: z.string(),
  center: z.object({ lat: z.number(), long: z.number() }),
  boundary: z.array(z.object({ lat: z.number(), long: z.number() })),
});

export type RegionWithCoords = z.infer<typeof regionWithCoordsSchema>;

export const addressSchema = z.object({
  name: z.string().nullable(),
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
});

export type Address = z.infer<typeof addressSchema>;

export const addressWithLatLongSchema = addressSchema.extend({
  lat: z.number().nullable(),
  long: z.number().nullable(),
});

export type AddressWithLatLong = z.infer<typeof addressWithLatLongSchema>;

export const driverSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  phone: z.string().optional(),
  cdlNumber: z.string().optional(),
  truckId: z.string().optional(),
});

export type Driver = z.infer<typeof driverSchema>;

export const claimStateSchema = z.enum(["open", "closed"]);
export type claimState = z.infer<typeof claimStateSchema>;

export const loadStateOptions = [
  "Not Assigned",
  "Assigned",
  "Being Picked Up",
  "In Transit",
  "Delivered",
  "Cancelled",
] as const;
export const loadStateSchema = z.enum(loadStateOptions);
export type LoadState = z.infer<typeof loadStateSchema>;

export const loadEventTypeOptions = [
  "Created",
  "Location Updated",
  "Carrier Updated",
  "Details Updated",
  "Notes Updated",
  "State Changed",
] as const;
export const loadEventTypeSchema = z.enum(loadEventTypeOptions);
export type LoadEventType = z.infer<typeof loadEventTypeSchema>;


const locationUpdatedMetadataSchema = z.object({
  city: z.string(),
  state: z.string(),
});
const stateChangedMetadataSchema = z.object({
  newState: loadStateSchema,
});


export const loadEventMetadataSchemas = {
  Created: null,
  "Location Updated": locationUpdatedMetadataSchema,
  "Carrier Updated": null,
  "Details Updated": null,
  "Notes Updated": null,
  "State Changed": stateChangedMetadataSchema,
} as const;

const metadataSchema = z.union([
  locationUpdatedMetadataSchema, 
  stateChangedMetadataSchema,
]);


export const loadEventSchema = z.object({
  id: z.string(),
  loadId: z.string(),
  eventType: loadEventTypeSchema,
  description: z.string(),
  createdAt: dateSchema,
  metadata: metadataSchema.nullable(),
});

export type LoadEvent<T extends LoadEventType & keyof LoadEventMetadata = LoadEventType & keyof LoadEventMetadata> = {
  id: string;
  loadId: string;
  eventType: T;
  description: string;
  createdAt: Date;
  metadata: LoadEventMetadata[T];
};

export type LoadEventMetadata = {
  [K in LoadEventType]: typeof loadEventMetadataSchemas[K] extends null ? null
    : typeof loadEventMetadataSchemas[K] extends z.ZodTypeAny
      ? z.infer<typeof loadEventMetadataSchemas[K]>
    : never;
};

